import React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { de, enGB, es, fr, it, nl, pt } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { appLocale } from "./Shared";

const LOCALES = { en: enGB, fr, nl, de, es, it, pt };
Object.entries(LOCALES).forEach(([code, locale]) => registerLocale(code, locale));

const FORMATS: Record<string, { date: string; hint: string }> = {
  en: { date: "dd/MM/yyyy", hint: "dd/mm/yyyy" },
  fr: { date: "dd/MM/yyyy", hint: "jj/mm/aaaa" },
  nl: { date: "dd-MM-yyyy", hint: "dd-mm-jjjj" },
  de: { date: "dd.MM.yyyy", hint: "TT.MM.JJJJ" },
  es: { date: "dd/MM/yyyy", hint: "dd/mm/aaaa" },
  it: { date: "dd/MM/yyyy", hint: "gg/mm/aaaa" },
  pt: { date: "dd/MM/yyyy", hint: "dd/mm/aaaa" },
};

const pad = (n: number) => String(n).padStart(2, "0");

const toValue = (d: Date, withTime: boolean) => {
  const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${day}T${pad(d.getHours())}:${pad(d.getMinutes())}` : day;
};

const parseValue = (v?: string | null) => {
  if (!v) return null;
  const [y, m, d, hh = 0, mm = 0] = v.split(/[-T:]/).map(Number);
  const date = new Date(y, m - 1, d, hh, mm);
  return isNaN(date.getTime()) ? null : date;
};

/** Date (YYYY-MM-DD) or date and time (YYYY-MM-DDTHH:mm) input that follows the app language, not the browser's. */
export const DateField: React.FC<{
  id?: string;
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: () => void;
  withTime?: boolean;
  className?: string;
}> = ({ id, value, onChange, onBlur, withTime = false, className = "form-control" }) => {
  useTranslation(); // re-renders when the language changes
  const locale = appLocale();
  const format = FORMATS[locale] || FORMATS.en;
  return (
    <DatePicker
      id={id}
      selected={parseValue(value)}
      onChange={(d: Date | null) => onChange(d ? toValue(d, withTime) : "")}
      onBlur={onBlur}
      locale={locale}
      dateFormat={withTime ? `${format.date} HH:mm` : format.date}
      placeholderText={withTime ? `${format.hint} HH:mm` : format.hint}
      showTimeSelect={withTime}
      timeFormat="HH:mm"
      timeIntervals={15}
      className={className}
      wrapperClassName="aces-date"
      autoComplete="off"
    />
  );
};
